import React, {useEffect, useMemo, useState}		from 'react';
import moment										from 'moment';
import {Area, AreaChart, CartesianGrid, Cell, LabelList,
	Legend, Pie, PieChart, ResponsiveContainer,
	Tooltip, TooltipProps, XAxis, YAxis}			from 'recharts';

import Header										from '../Header/Header';
import Icon											from '../ressources/components/Icon';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {getDashboardApi}							from '../redux/dashboard/dashboard';
import {DashboardNotification}						from '../redux/dashboard/interface';

import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_checkRight}								from '../ressources/functions/userFunction';
import {ft_formatFirstUpperCase}					from '../ressources/functions/format';
import {DEVICE_TYPE}								from '../ressources/data/form';
import useWindowSize from '../ressources/functions/useWindowSize';

import img_device									from '../ressources/images/icon/devices/computer.svg';
import img_box										from '../ressources/images/icon/box.svg';
import img_contract									from '../ressources/images/icon/menu/contract.svg';
import img_sav										from '../ressources/images/icon/menu/help.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Dashboard.css';


function Dashboard() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const dashboard = useAppSelector(state => state.dashboard.data);
	const lang = useAppSelector(state => state.settings.lang);
	const userReload = useAppSelector(state => state.user.reload);

	const [notifications, set_notifications] = useState<DashboardNotification[]>([]);

	const TEXTE = LANGUAGE(lang);

	const limited = useMemo(() => !ft_checkRight(user, 'company.read'), [user]);
	const sizes = useWindowSize();

	useEffect(() => {
		dispatch(getDashboardApi(user));
	}, [userReload]);

	useEffect(getNotification, [open, userReload]);

	function getNotification() {
		API.getDashboardNotification(user)
		.then((response) => {
			if (response.status && response.data)
				return set_notifications(response.data);
		});
	}

	// function closeNotification(id: string) {
	// 	API.closeDashboardNotification(id, user)
	// 	.then(getNotification);
	// }

	function renderStatBox(icon: string, name: string, number?: (number | string), textColor?: string) {
		if (!number)
			number = 0;

		return (
			<div className={'dashboardStatBox'}>
				<div>
					<Icon
						icon={icon}
						iconSize={50}
						iconColor={ft_cssVar('primary-600')}
					/>
				</div>
				<div>
					<span className={`display-sm weight-600 gray-900 ${textColor}`}>{number}</span>
					<span className={'text-md weigth-500 gray-600'}>{name}</span>
				</div>
			</div>
		);
	}

	// function renderNotifications() {
	// 	const render: React.ReactElement[] = [];
	// 	let first = true;

	// 	notifications?.forEach((notification, i) => {
	// 		render.push(
	// 			<div key={i} className={`dashboardNotification ${first && 'first'}`}>
	// 				{ft_getDashboardNotificationIcon(notification.name)}
	// 				<div className={'dashboardNotificationText text-sm weight-400 gray-600'}>
	// 					{ft_genDashboardNotification(TEXTE, navigate, notification.name, notification.vars)}
	// 				</div>
	// 				<div className={'flex'} />
	// 				<div onClick={() => closeNotification(notification._id)}>
	// 					<Icon
	// 						flex
	// 						icon={img_check}
	// 						popup={TEXTE.dashboard.setRead}
	// 						iconColor={ft_cssVar('gray-700')}
	// 					/>
	// 				</div>
	// 			</div>
	// 		);

	// 		first = false;
	// 	});

	// 	return render;
	// }

	function renderAreaChart() {
		const data: {name: string, value: string, count: number}[] = [];

		dashboard?.contract?.months?.forEach((month) => {
			data.push({
				count: month.count,
				value: (month.quantity || 0).toFixed(),
				name:  moment(month.month).format('MM/YY')
				// value: (month.total || 0).toFixed(2),
			});
		});

		if (!data.length)
			data.push({name: moment().subtract(1, 'month').format('MM/YY'), value: '0', count: 0});

		let maxValue = Math.max(...data.map(d => parseFloat(d.value))); // Trouve la plus grande valeur

		maxValue *= 1.4;
		const orderOfMagnitude = 10 ** Math.floor(Math.log10(maxValue));

		const step = orderOfMagnitude / 2;
		const yMax = Math.ceil(maxValue / step) * step;
		const yTicks = Array.from({length: 5}, (_, i) => ((i * yMax) / 4).toFixed());

		const CustomTooltip: React.FC<TooltipProps<number, string>> = ({active, payload}) => {
			if (!active || !payload?.length)
				return null;

			const {name, value, count} = payload[0].payload;

			return (
				<div className={'dashboardAreaTooltip'}>
					<span className={'gray-500'}>{ft_formatFirstUpperCase(moment(name, 'MM/YY').format('MMMM YYYY'))}</span>
					<br />
					<strong className={'text-lg'}>{`${value} ${TEXTE.global.devices}`}</strong>
					{/* <strong className={'text-lg'}>{ft_formatPrice(value)}</strong> */}
					<br />
					<span className={'gray-600'}>{`${count} ${TEXTE.menu.contracts.toLowerCase()}`}</span>
				</div>
			);
		};

		return (
			<ResponsiveContainer width="100%" height="80%" style={{margin: 'auto'}}>
				<AreaChart
					data={data}
					margin={{right: 25}}
				>
					<defs>
						<linearGradient id="fadeGradient" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={ft_cssVar('primary-400')} stopOpacity={1} />
							<stop offset="100%" stopColor={ft_cssVar('primary-400')} stopOpacity={0.1} />
						</linearGradient>
					</defs>
					<CartesianGrid vertical={false} />
					<XAxis dataKey={'name'} />
					<YAxis ticks={yTicks} domain={[0, yMax]} />
					<Tooltip content={<CustomTooltip />} />
					<Area
						name={'Appareils loués'}
						dataKey={'value'}
						type={'monotone'}
						fill={'url(#fadeGradient)'}
						stroke={ft_cssVar('primary-500')}
					/>
					<Legend />
				</AreaChart>
			</ResponsiveContainer>
		);
	}

	function renderPieChart() {
		const typeCount: { name: string; value: number }[] = [];

		DEVICE_TYPE(TEXTE).forEach((type) => {
			const findType = dashboard?.devices?.types?.find(find => find.name === type.value || find.name === type.render);

			if (findType)
				typeCount.push({name: type.render, value: findType.count});
		});

		typeCount.sort((a, b) => b.value - a.value);

		const topThree = typeCount.slice(0, 3);
		const otherTypes = typeCount.slice(3);
		const otherTotal = otherTypes.reduce((sum, entry) => sum + entry.value, 0);

		if (otherTotal > 0)
			topThree.push({name: 'Autres', value: otherTotal});

		topThree.sort((a, b) => b.value - a.value);

		const total = topThree.reduce((sum, entry) => sum + entry.value, 0);

		const CustomTooltip: React.FC<TooltipProps<number, string>> = ({active, payload}) => {
			if (!active || !payload?.length)
				return null;

			const {name, value} = payload[0].payload;

			return (
				<div style={{background: 'white', padding: 10, borderRadius: 5, border: '1px solid #ccc'}}>
					{name === 'Autres' ?
						<>
							<strong>{`${name} (${value})`}</strong>
							<ul style={{margin: 0, paddingLeft: 15}}>
								{otherTypes.map((item, i) => <li key={i}>{`${item.name}: ${item.value}`}</li>)}
							</ul>
						</>
						:
						<><strong>{name}</strong>{`:${value}`}</>
					}
				</div>
			);
		};

		const COLORS = ['primary-400', 'warning-400', 'error-400', 'success-400'];

		return (
			<ResponsiveContainer width={'100%'} height={'80%'} style={{margin: 'auto', overflow: 'visible'}}>
				<PieChart>
					<Pie
						data={topThree}
						paddingAngle={2}
						nameKey={'name'}
						dataKey={'value'}
						outerRadius={sizes.width >= 1440 ? '65%' : '50%'}
						innerRadius={sizes.width >= 1440 ? '40%' : '20%'}
						label={e => e.name}
					>
						{topThree.map((entry, index) => (
							<Cell key={index} fill={ft_cssVar(COLORS[index % COLORS.length])} />
						))}
						<LabelList
							fill={'white'}
							position={'inside'}
							dataKey={e => `${((e.value / total) * 100).toFixed(1)}%`}
						/>
					</Pie>
					<Tooltip content={<CustomTooltip />} />
				</PieChart>
			</ResponsiveContainer>
		);
	}

	return (
		<div className={'globalContainerWithHeader'}>
			<Header
				title={TEXTE.dashboard.title}
				badgeText={notifications?.length}
			/>

			<div className={'dashboardMain'}>
				<div className={'dashboardStatContainer'}>
					{!limited && renderStatBox(img_device, TEXTE.dashboard.inventoryDevice, dashboard?.devices?.count)}
					{!limited && renderStatBox(img_box, TEXTE.dashboard.inStockDevice, dashboard?.devices?.inStock)}

					{renderStatBox(img_contract, TEXTE.dashboard.endSoon, dashboard?.contract?.finish3)}

					{renderStatBox(img_sav, TEXTE.dashboard.openTicket, dashboard?.support?.count)}
				</div>

				<div className={'flex'} style={{gap: 16}}>
					<div className={'flex dashboardItem'} style={{flex: 1.5}}>
						<span className={'text-xl weight-500 gray-600'}>
							{TEXTE.dashboard.area}
						</span>
						<br />
						{renderAreaChart()}
					</div>
					<div className={'flex dashboardItem'} style={{padding: 'unset', overflow: 'visible'}}>
						<span className={'text-xl weight-500 gray-600'} style={{margin: '16px 24px'}}>
							{TEXTE.dashboard.pie}
						</span>
						<br />
						{renderPieChart()}
					</div>
				</div>

				{/* <div className={'dashboardItem'}>
					{renderNotifications()}
				</div> */}
			</div>
		</div>
	);
}

export default Dashboard;
